import { authorizedFetch } from "@/utils/fetch";

export enum Tags {
  SAFE = "safe",
  UNSAFE = "unsafe",
}

export enum CommentStatus {
  PENDING = "pending",
  ACCEPTED = "accepted",
  DELETED = "deleted",
  WILL_BE_DELETED = "willBeDeleted",
  WILL_BE_ACCEPTED = "willBeAccepted",
  ERROR_ON_DELETE = "deleteError",
  HIDDEN = "hidden",
  HIDDEN_BY_PARENT = "hiddenByParent",
  WILL_BE_HIDDEN = "willBeHidden",
  WILL_BE_UNHIDDEN = "willBeUnhidden",
  ERROR_ON_HIDE = "hideError",
  ERROR_ON_UNHIDE = "unhideError",
  HIDDEN_FOR_DELETEION = "hiddenForDeletion",
  ERROR_ON_HIDE_FOR_DELETION = "hideForDeleteError",
}

export enum CommentProperty {
  PINNED = "pinned",
  UNPINNED = "unpinned",
  VISIBLE = "visible",
  NOT_VISIBLE = "notVisible",
  UNHIDE_DISABLED = "unhideDisabled",
}

export type Comment = {
  commentProperties: CommentProperty[];
  id: string;
  threadId: string;
  parentId: string;
  user: {
    id: string;
    externalId: string;
    profilePictureUrl: string;
    userName: string;
    realName: string;
    email: string;
    followerscCount: number;
  };
  post: { id: number; externalId: string; teaser: string };
  text: string;
  tags: { name: string; markupElements: string[]; score: number }[];
  labelIds: number[];
  createdAt: string;
  modifiedAt: string;
  status: CommentStatus;
  children: Comment[];
  unsafetyScore: number;
  isUnsafe: boolean;
  unsafetyReason: string;
  similarCommentHash: null | string;
  similarComment: boolean;
  // Thread-specific fields (set when using getCommentThreads endpoint)
  hasMoreReplies?: boolean;
  externalCommentId?: string;
  pageId?: string;
  permalinkUrl?: string;
  likeCount?: number;
};

const baseUrl = import.meta.env.VITE_API_URL;
const baseUrlV1 = `${baseUrl}/api/v1`;
const baseUrlV2 = `${baseUrl}/api/v2`;

const urls = {
  comments: `${baseUrlV1}/comments`,
  commentsV2: `${baseUrlV2}/comments`,
  adminComments: `${baseUrlV1}/admin/comments`,
} as const;

export type CommentFilters = {
  from?: string;
  to?: string;
  visibilities?: string[];
  attributes?: string[];
  tags?: string[];
  text?: string[];
  connection?: string;
  sort?: string;
  order?: string;
  statuses?: string[];
  status?: string;
  unsafe?: string | boolean;
  user?: string;
  post?: string;
  watchlist?: string;
  watchlistType?: string;
  isExtendedSearch?: boolean;
  commentIds?: string[];
  hash?: string;
  statusConnection?: string;
};

export type QuickFilter = {
  id?: number;
  name: string;
  appUserId?: number;
  appUserFirstName: string;
  appUserLastName: string;
  settings: {
    id: number;
    fromDate: null | string;
    toDate: null | string;
    commentProperties: string[];
    aiCommentTags: string[];
    textSearch: string[];
    connection: string;
    sortingBy: number;
    orderBy: string;
    commentStatus: string[];
    isUnsafe: boolean;
  };
  private: boolean;
};

export type ReplyTemplate = {
  id: string;
  name: string;
  reply: string;
};

export type Tag = {
  id: number;
  name: string;
  isRequired: boolean;
  isFilter: boolean;
  type: string;
};

export type CommentWatchlist = {
  id: number;
  smc: number;
  smt: number;
  name: string;
  creatorId: number;
  isPublic: boolean;
  creatorFirstName: string;
  creatorLastName: string;
};

export type CommentWatchlistBody = {
  name?: string;
  isPublic?: boolean;
  itemIds?: string[];
  isAdded?: boolean;
};

export type ReplyToCommentPayload = {
  parentCommentId: string;
  text: string;
  socialMediaConnectionId: number;
  socialMediaTypeId: number;
};

export type CommentCounts = {
  folders: Record<string, number>;
  watchlists: Record<string, number>;
};

export type CommentLabelResponse = {
  tags: {
    id: number;
    name: string;
    isRequired: boolean;
  }[];
};

export const convertQuickFiltersToFilters = (quickFilter: QuickFilter): CommentFilters => {
  return {
    statuses: quickFilter.settings.commentStatus || [],
    tags: quickFilter.settings.aiCommentTags || [],
    visibilities: quickFilter.settings.commentProperties || [],
    text: quickFilter.settings.textSearch || [],
    unsafe: !!quickFilter.settings.isUnsafe,
  };
};

export type DeleteCommentsPayload = {
  ids: string[];
  socialMediaConnectionId: number;
  pageExternalId: string;
  socialMediaTypeId: number;
  postExternalId: string;
  priorityLevel: number;
};

type CommentIdResponse = Record<string, string[]>[];

export type CommentIds = { id: string; highlightedCommentIds: string[] }[];

export type CommentPermalinkResponse = {
  commentId: string;
  permalinkUrl?: string;
  isFound: boolean;
  errorMessage?: string;
};

export const getNumberOfComments = async (
  socialMediaTypeId: number,
  socialMediaConnectionId: number,
  filters?: CommentFilters,
): Promise<CommentCounts> => {
  const params = new URLSearchParams();
  params.set("smc", socialMediaConnectionId.toString());
  params.set("smt", socialMediaTypeId.toString());
  if (filters?.unsafe) params.set("unsafe", filters.unsafe.toString());
  if (filters?.user) params.set("user", filters.user.toString());
  if (filters?.statuses) filters.statuses.forEach((item) => params.append("status", item));
  if (filters?.watchlist) params.set("wl", filters.watchlist);
  if (filters?.watchlistType) params.set("wl-type", filters.watchlistType);
  const url = `${urls.comments}/count?${params.toString()}`;
  const response = await authorizedFetch(url);
  const commentCounts: CommentCounts = await response.json();
  return commentCounts;
};

export const refreshCommentThread = async (id: string) => {
  const url = `${urls.comments}/${id}/refresh`;
  await authorizedFetch(url);
  return;
};

export const acceptComments = async (commentIds: string[], socialMediaType: number = 1) => {
  const url = `${urls.comments}/bulk/accept`;
  await authorizedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ 
      ids: commentIds,
      socialMediaType: socialMediaType
    }),
  });
};

export const deleteCommentsBulk = async (commentIds: string[], socialMediaType: number = 1) => {
  const url = `${urls.comments}/bulk/delete`;
  await authorizedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ 
      ids: commentIds,
      socialMediaType: socialMediaType
    }),
  });
};

export const deleteComments = async (payload: DeleteCommentsPayload) => {
  const url = `${urls.comments}/bulk/delete`;
  await authorizedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ids: payload.ids,
      socialMediaType: payload.socialMediaTypeId
    }),
  });
};

export const hideComments = async (commentIds: string[], socialMediaType: number = 1) => {
  const url = `${urls.comments}/bulk/hide`;
  await authorizedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ 
      ids: commentIds,
      socialMediaType: socialMediaType
    }),
  });
};

export const revealComments = async (commentIds: string[], socialMediaType: number = 1) => {
  const url = `${urls.comments}/bulk/reveal`;
  await authorizedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ 
      ids: commentIds,
      socialMediaType: socialMediaType
    }),
  });
};

export const setCommentPinned = async (commentId: string, isPinned: boolean, socialMediaType: number = 1) => {
  const url = `${urls.comments}/pin`;
  await authorizedFetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      commentId,
      isPinned,
      socialMediaType,
    }),
  });
};

export const getCommentLabels = async (): Promise<CommentLabelResponse> => {
  const url = `${baseUrlV1}/comment-labels/all`;
  const response = await authorizedFetch(url);
  const labels = response.json();
  return labels;
};

export const setCommentLabel = async (commentId: string, label: string, isAdded: boolean) => {
  const url = `${urls.comments}/${commentId.toString()}/label`;
  await authorizedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      label: label,
      isAdded: isAdded,
    }),
  });
};

export const getCommentReplyTemplates = async (): Promise<ReplyTemplate[]> => {
  const url = `${urls.comments}/template`;
  const response = await authorizedFetch(url);
  const templates = await response.json();
  return templates.replyTemplates;
};

export const saveCommentReplyTemplate = async (body: { name: string; text: string }) => {
  const url = `${urls.comments}/template`;
  const response = await authorizedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: body.name,
      reply: body.text,
    }),
  });
  const result = await response.json();
  return result.id;
};

export const editCommentReplyTemplate = async (
  templateId: string,
  body: { name: string; text: string },
) => {
  const url = `${urls.comments}/template/${templateId}`;
  await authorizedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: body.name,
      reply: body.text,
    }),
  });
};

export const deleteCommentReplyTemplate = async (
  templateId: string,
  body: { name: string; text: string },
) => {
  const url = `${urls.comments}/template/${templateId}`;
  await authorizedFetch(url, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: body.name,
      reply: body.text,
    }),
  });
};

export const replyToComment = async (body: ReplyToCommentPayload) => {
  const url = `${urls.comments}/reply`;
  await authorizedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      socialMediaConnectionId: body.socialMediaConnectionId,
      socialMediaTypeId: body.socialMediaTypeId,
      parentCommentId: body.parentCommentId,
      text: body.text,
    }),
  });
};

export const isCommentDeleted = (commentStatus: CommentStatus) => {
  return commentStatus === CommentStatus.DELETED || commentStatus === CommentStatus.WILL_BE_DELETED;
};

export const getQuickFilters = async (admin?: boolean): Promise<QuickFilter[]> => {
  const url = admin ? `${urls.adminComments}/quick-filters` : `${urls.comments}/quick-filters`;
  const quickFilters = await authorizedFetch(url);
  return quickFilters.json();
};

export const deleteQuickFilter = async (id: number) => {
  const url = `${urls.comments}/quick-filters/${id}`;
  await authorizedFetch(url, {
    method: "DELETE",
  });
};

// Todo: take care during quick filter implementation. adjustments needed
export const createQuickFilter = async (
  isPrivate: boolean,
  quickFilterName: string,
  filters: CommentFilters,
) => {
  const body = {
    private: isPrivate,
    name: quickFilterName,
    settings: {
      commentProperties: filters.visibilities,
      aiCommentTags: filters.tags,
      text: filters.text,
      commentStatus: filters.statuses,
      isUnsafe: filters.unsafe,
    },
  };
  const url = `${urls.comments}/quick-filters`;
  await authorizedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
};

// Todo: adjust implementation during quick filter implemenation
export const updateQuickFilter = async (
  isPrivate: boolean,
  quickFilterName: string,
  filters: CommentFilters,
  id: number,
) => {
  const body = {
    private: isPrivate,
    name: quickFilterName,
    settings: {
      commentProperties: filters.visibilities,
      aiCommentTags: filters.tags,
      text: filters.text,
      commentStatus: filters.statuses,
      isUnsafe: filters.unsafe,
    },
  };
  const url = `${urls.comments}/quick-filters/${id}`;
  await authorizedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
};

export const setCommentsConfidenceScore = async (score: string) => {
  const url = `${urls.adminComments}/confidence-score?threshold=${score}`;
  await authorizedFetch(url, {
    method: "POST",
  });
};

export const getCommentsConfidenceScore = async () => {
  const url = `${urls.comments}/confidence-score`;
  const confidenceScore = await authorizedFetch(url);
  return confidenceScore.json();
};

export const getAICommentTags = async (): Promise<{ tags: Tag[] }> => {
  const url = `${baseUrlV1}/ai-tags/all`;
  const response = await authorizedFetch(url);
  const tags = response.json();
  return tags;
};

export const createCommentWatchlist = async (
  socialMediaTypeId: number,
  socialMediaConnectionId: number,
  isPublic: boolean,
  watchlistName: string,
  ids: string[],
) => {
  const body = {
    smt: socialMediaTypeId,
    smc: socialMediaConnectionId,
    isPublic: isPublic,
    name: watchlistName,
    ids: ids,
  };
  const url = `${baseUrlV1}/watchlist/comments`;
  await authorizedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
};

export const deleteCommentWatchlist = async (id: number) => {
  const url = `${baseUrlV1}/watchlist/comments/${id}`;
  await authorizedFetch(url, {
    method: "DELETE",
  });
};

export const updateWatchlist = async (id: number, body: CommentWatchlistBody) => {
  const url = `${baseUrlV1}/watchlist/comments/${id}`;
  await authorizedFetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
};

export const getCommentWatchlists = async (isAdmin = false): Promise<CommentWatchlist[]> => {
  const admin = isAdmin ? "/admin" : "";
  const url = `${baseUrlV1}${admin}/watchlist/comments`;
  const response = await authorizedFetch(url);
  const watchlists = await response.json();
  return watchlists.watchlists;
};

export type CommentThreadsApiResponse = {
  comments: Comment[];
  totalCommentsCount: number;
  totalThreadCount: number;
  highlightedCommentIds: string[];
  page: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
};

export type ThreadRepliesApiResponse = {
  replies: Comment[];
  hasNextPage: boolean;
  nextCursorPublishedAt: string | null;
  nextCursorId: string | null;
  /** IDs matching the active folder/status filter — mirrors highlightedCommentIds from the threads endpoint */
  highlightedCommentIds: string[];
};

export const getCommentThreads = async (
  socialMediaTypeId: number,
  filters: CommentFilters,
  page: number = 1,
  pageSize: number = 20,
  abortSignal?: AbortSignal,
): Promise<CommentThreadsApiResponse> => {
  const params = new URLSearchParams();
  params.set("smt", socialMediaTypeId.toString());
  params.set("page", page.toString());
  params.set("pageSize", pageSize.toString());
  params.set("children", "true");

  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.sort) params.set("sort", filters.sort);
  if (filters.order) params.set("order", filters.order);
  if (filters.statuses) params.set("status", filters.statuses.join(","));
  if (filters.text) filters.text.forEach(t => params.append("se", t));
  if (filters.attributes) params.set("ab", filters.attributes.join(","));
  if (filters.post) params.set("postId", filters.post);
  if (filters.user) params.set("userId", filters.user);
  if (filters.unsafe === true || filters.unsafe === "true")
    params.set("unsafe-only", "true");
  if (filters.tags && filters.tags.length > 0)
    params.set("tags", filters.tags.join(","));
  if (filters.hash) params.set("s-hash", filters.hash);
  if (filters.commentIds) params.set("ids", filters.commentIds.join(","));
  if (filters.statusConnection) params.set("s-connection", filters.statusConnection);

  const url = `${baseUrlV1}/comments/threads?${params.toString()}`;
  const response = await authorizedFetch(url, { signal: abortSignal });
  return response.json();
};

export const getThreadReplies = async (
  threadCommentId: string,
  smt: number,
  channelId: string,
  pageSize: number = 100,
  lastSeenPublishedAt?: string,
  lastSeenId?: string,
  status?: string,
  unsafeOnly?: boolean,
  tags?: string[],
): Promise<ThreadRepliesApiResponse> => {
  const params = new URLSearchParams();
  params.set("smt", smt.toString());
  params.set("channelId", channelId);
  params.set("pageSize", pageSize.toString());
  if (lastSeenPublishedAt) params.set("lastSeenPublishedAt", lastSeenPublishedAt);
  if (lastSeenId) params.set("lastSeenId", lastSeenId);
  if (status) params.set("status", status);
  if (unsafeOnly) params.set("unsafe-only", "true");
  if (tags && tags.length > 0) params.set("tags", tags.join(","));

  const url = `${baseUrlV1}/comments/threads/${encodeURIComponent(threadCommentId)}/replies?${params.toString()}`;
  const response = await authorizedFetch(url);
  return response.json();
};

export const getCommentPermalink = async (
  commentId: string,
  externalCommentId: string,
  smt: number,
): Promise<CommentPermalinkResponse> => {
  const params = new URLSearchParams();
  params.set("externalCommentId", externalCommentId);
  params.set("smt", smt.toString());

  const url = `${urls.comments}/${commentId}/permalink?${params.toString()}`;
  const response = await authorizedFetch(url);
  return response.json();
};
